import { Howl, Howler } from 'howler';

export class AudioManager {
    constructor() {
        console.log('[AUDIO_MANAGER] Initializing AudioManager');
        // Initialize collections
        this.globalSounds = new Map();
        this.spatialSources = new Set();
        this.buffers = new Map();
        this.tileSize = 32;
        this.maxDistance = 10 * this.tileSize;
        this.maxBounces = 2;
        
        // Audio context state
        this.audioCtx = null;
        this.masterGain = null;
        this.audioInitialized = false;
        this.pendingSounds = new Map();
        
        // Initialize audio context immediately
        this.initPromise = this.initializeAudio();
    }

    async initializeAudio() {
        try {
            console.log('[AUDIO_MANAGER] Creating AudioContext');
            this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            this.masterGain = this.audioCtx.createGain();
            this.masterGain.connect(this.audioCtx.destination);
            this.audioInitialized = true;
            console.log('[AUDIO_MANAGER] AudioContext initialized');

            // Process any pending sounds
            for (const [url, name] of this.pendingSounds) {
                await this.loadSound(url, name);
            }
            this.pendingSounds.clear();
        } catch (error) {
            console.error('[AUDIO_MANAGER] Failed to initialize audio:', error);
            throw error;
        }
    }

    async loadSound(url, name) {
        console.log(`[AUDIO_MANAGER] Loading sound: ${name} from ${url}`);
        if (this.globalSounds.has(name)) {
            console.log(`[AUDIO_MANAGER] Sound ${name} already loaded`);
            return;
        }
        
        // If not initialized, queue the sound
        if (!this.audioInitialized) {
            console.log(`[AUDIO_MANAGER] Audio not initialized, queueing sound ${name}`);
            this.pendingSounds.set(url, name);
            return;
        }
        
        try {
            // Create Howl instance with default settings
            const howl = new Howl({
                src: [url],
                preload: true,
                onload: () => {
                    console.log(`[AUDIO_MANAGER] Successfully loaded sound: ${name}`);
                },
                onloaderror: (id, error) => {
                    console.warn(`[AUDIO_MANAGER] Failed to load sound ${name}, will be skipped:`, error);
                    this.globalSounds.delete(name);
                }
            });
            
            // Wait for Howl to be ready
            await new Promise((resolve, reject) => {
                if (howl.state() === 'loaded') {
                    resolve();
                } else {
                    howl.once('load', resolve);
                    howl.once('loaderror', () => {
                        console.warn(`[AUDIO_MANAGER] Sound ${name} failed to load, continuing without it`);
                        resolve(); // Resolve instead of reject to continue loading
                    });
                }
            });
            
            this.globalSounds.set(name, howl);
            console.log(`[AUDIO_MANAGER] Sound ${name} loaded and ready to play`);
        } catch (error) {
            console.warn(`[AUDIO_MANAGER] Error loading sound ${name}:`, error);
        }
    }

    playGlobalSound(name, options = {}) {
        if (!this.audioInitialized) {
            console.warn('[AUDIO_MANAGER] Audio not initialized, cannot play sound');
            return;
        }

        const sound = this.globalSounds.get(name);
        if (!sound) {
            console.warn(`[AUDIO_MANAGER] Sound ${name} not found`);
            return;
        }

        try {
            const id = sound.play();
            if (options.volume !== undefined) {
                sound.volume(options.volume, id);
            }
            if (options.loop) {
                sound.loop(true, id);
            }
            return id;
        } catch (error) {
            console.warn(`[AUDIO_MANAGER] Error playing sound ${name}:`, error);
        }
    }

    // Music is a looping global sound; remember which one so stopMusic() needs no name
    playMusic(name, loop = true) {
        if (this.currentMusic && this.currentMusic !== name) this.stopMusic();
        this.currentMusic = name;
        return this.playGlobalSound(name, { loop });
    }

    stopMusic() {
        if (!this.currentMusic) return;
        const sound = this.globalSounds.get(this.currentMusic);
        if (sound) sound.stop();
        this.currentMusic = null;
    }

    // Stop global sound
    stopGlobalSound(name) {
        console.log(`[AUDIO_MANAGER] Stopping global sound: ${name}`);
        const sound = this.globalSounds.get(name);
        if (sound) {
            sound.stop();
        } else {
            console.warn(`[AUDIO_MANAGER] Sound ${name} not found`);
        }
    }

    // Fade out global sound
    fadeOutGlobalSound(name, duration = 1.0) {
        const sound = this.globalSounds.get(name);
        if (sound) {
            sound.fade(sound.volume(), 0, duration * 1000);
            setTimeout(() => sound.stop(), duration * 1000);
        }
    }

    // Play spatial sound (e.g., footsteps) with Web Audio
    playSpatialSound(name, x, y, { volume = 1.0 } = {}) {
        if (!this.buffers.has(name)) return;
        const buffer = this.buffers.get(name);
        const source = this.audioCtx.createBufferSource();
        source.buffer = buffer;

        const panner = this.audioCtx.createPanner();
        panner.positionX.value = x;
        panner.positionY.value = y;
        panner.distanceModel = 'inverse';
        panner.refDistance = this.tileSize;
        panner.maxDistance = this.maxDistance;

        const gainNode = this.audioCtx.createGain();
        gainNode.gain.value = volume;

        // Connect nodes in the correct order
        source.connect(panner);
        panner.connect(gainNode);
        gainNode.connect(this.masterGain);
        
        source.start();

        const sound = { source, panner, gainNode, pos: { x, y }, name };
        this.spatialSources.add(sound);
        source.onended = () => this.spatialSources.delete(sound);
        return sound;
    }

    // Update spatial sounds with raytracing
    updateSpatialSounds(listenerX, listenerY, world) {
        for (const sound of this.spatialSources) {
            const { pos, gainNode, panner, name } = sound;

            // Direct path raycasting
            const direct = this.traceRay(pos, { x: listenerX, y: listenerY }, world);
            let volume = 1.0 / (direct.path.length * this.tileSize + 1) ** 2;
            if (direct.blocked) volume *= (1 - (direct.hitTile?.acoustic?.occlusion || 0.8));

            // Reflections
            const reflections = this.traceReflections(pos, world);
            for (const refPath of reflections) {
                const lastPoint = refPath.path.at(-1);
                const toListener = this.traceRay(
                    { x: lastPoint.x * this.tileSize, y: lastPoint.y * this.tileSize },
                    { x: listenerX, y: listenerY },
                    this.maxDistance - refPath.distance,
                    world
                );
                if (!toListener.blocked) {
                    const totalDistance = refPath.distance + toListener.path.length * this.tileSize;
                    const delay = totalDistance / 343; // Sound speed in m/s
                    const refVolume = volume * (1 - (refPath.path[0]?.absorption || 0.1)) ** refPath.bounces;
                    this.playSpatialSound(name, lastPoint.x * this.tileSize, lastPoint.y * this.tileSize, {
                        volume: refVolume,
                    });
                    // Delay playback
                    sound.source.start(this.audioCtx.currentTime + delay);
                }
            }

            gainNode.gain.value = volume;
            panner.positionX.value = pos.x;
            panner.positionY.value = pos.y;
        }
    }

    // Simplified raycasting (Bresenham's algorithm)
    traceRay(start, end, world) {
        const path = [];
        let x0 = Math.floor(start.x / this.tileSize);
        let y0 = Math.floor(start.y / this.tileSize);
        let x1 = Math.floor(end.x / this.tileSize);
        let y1 = Math.floor(end.y / this.tileSize);

        const dx = Math.abs(x1 - x0);
        const dy = Math.abs(y1 - y0);
        const sx = x0 < x1 ? 1 : -1;
        const sy = y0 < y1 ? 1 : -1;
        let err = dx - dy;

        let distance = 0;
        while (distance < this.maxDistance / this.tileSize) {
            const tile = world[y0]?.[x0];
            if (!tile || tile.type === 'wall') {
                return { path, blocked: true, hitTile: tile };
            }
            path.push({ x: x0, y: y0, distance: distance * this.tileSize });
            if (x0 === x1 && y0 === y1) break;

            const e2 = 2 * err;
            if (e2 > -dy) {
                err -= dy;
                x0 += sx;
            }
            if (e2 < dx) {
                err += dx;
                y0 += sy;
            }
            distance++;
        }
        return { path, blocked: false };
    }

    // Simplified reflection raycasting
    traceReflections(source, world) {
        const directions = [
            { dx: 1, dy: 0 }, { dx: -1, dy: 0 }, { dx: 0, dy: 1 }, { dx: 0, dy: -1 },
        ];
        const paths = [];
        for (const dir of directions) {
            let pos = { x: source.x, y: source.y };
            let bounces = 0;
            let distance = 0;
            let path = [];
            while (bounces < this.maxBounces && distance < this.maxDistance) {
                const result = this.traceRay(pos, {
                    x: pos.x + dir.dx * this.tileSize,
                    y: pos.y + dir.dy * this.tileSize,
                }, world);
                path.push(...result.path);
                distance += result.path.length * this.tileSize;
                if (result.blocked && result.hitTile?.acoustic?.absorption < 0.9) {
                    if (dir.dx !== 0) dir.dx *= -1;
                    if (dir.dy !== 0) dir.dy *= -1;
                    bounces++;
                    pos = { x: result.path.at(-1).x * this.tileSize, y: result.path.at(-1).y * this.tileSize };
                } else {
                    break;
                }
            }
            if (path.length) paths.push({ path, distance, bounces });
        }
        return paths;
    }

    // Set master volume
    setMasterVolume(volume) {
        this.masterGain.gain.value = Math.max(0, Math.min(1, volume));
        Howler.volume(volume);
    }

    // Suspend/resume for pause or tab focus
    suspend() {
        Howler.mute(true);
        return this.audioCtx.suspend();
    }

    resume() {
        Howler.mute(false);
        return this.audioCtx.resume();
    }

    // Cleanup
    destroy() {
        // Stop all sounds
        this.globalSounds.forEach(sound => sound.stop());
        this.spatialSources.forEach(sound => sound.source.stop());
        
        // Clear collections
        this.globalSounds.clear();
        this.spatialSources.clear();
        this.buffers.clear();
        
        // Remove event listener
        document.removeEventListener('click', this.handleClick);
        
        // Close audio context if it exists and isn't already closed
        if (this.audioCtx && this.audioCtx.state !== 'closed') {
            this.audioCtx.close();
        }
    }
} 